import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { funnelApi } from '../api/funnel-api';
import { FunnelStageInfo, FunnelStage } from '../types/crm-types';

// === ASYNC THUNKS ===

export const fetchFunnelStages = createAsyncThunk(
  'funnel/fetchFunnelStages',
  async (_, { rejectWithValue }) => {
    try {
      const response = await funnelApi.getFunnelStages();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Error al cargar las etapas del funnel');
    }
  }
);

export const fetchFunnelStageInfo = createAsyncThunk(
  'funnel/fetchFunnelStageInfo',
  async (stage: string, { rejectWithValue }) => {
    try {
      const response = await funnelApi.getFunnelStageInfo(stage);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Error al cargar la información de la etapa');
    }
  }
);

export const fetchFunnelStageValues = createAsyncThunk(
  'funnel/fetchFunnelStageValues',
  async (_, { rejectWithValue }) => {
    try {
      const response = await funnelApi.getFunnelStageValues();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Error al cargar los valores de las etapas');
    }
  }
);

// === STATE INTERFACE ===

interface FunnelState {
  // Datos
  stages: FunnelStageInfo[];
  stageValues: FunnelStage[];
  currentStageInfo: FunnelStageInfo | null;
  
  // Estados de carga
  loading: {
    stages: boolean;
    stageInfo: boolean;
    stageValues: boolean;
  };
  
  // Estados de error
  error: {
    stages: string | null;
    stageInfo: string | null;
    stageValues: string | null;
  };
  
  // Cache
  lastFetch: {
    stages: number | null;
    stageInfo: number | null;
    stageValues: number | null;
  };
}

// === INITIAL STATE ===

const initialState: FunnelState = {
  stages: [],
  stageValues: [],
  currentStageInfo: null,
  
  loading: {
    stages: false,
    stageInfo: false,
    stageValues: false,
  },
  
  error: {
    stages: null,
    stageInfo: null,
    stageValues: null,
  },
  
  lastFetch: {
    stages: null,
    stageInfo: null,
    stageValues: null,
  },
};

// === SLICE ===

const funnelSlice = createSlice({
  name: 'funnel',
  initialState,
  reducers: {
    // Limpiar errores
    clearErrors: (state) => {
      state.error = {
        stages: null,
        stageInfo: null,
        stageValues: null,
      };
    },
    
    // Limpiar estado actual
    clearCurrentStageInfo: (state) => {
      state.currentStageInfo = null;
      state.error.stageInfo = null;
    },
    
    // Resetear estado completo
    resetFunnelState: () => initialState,
  },
  extraReducers: (builder) => {
    // === FETCH FUNNEL STAGES ===
    builder
      .addCase(fetchFunnelStages.pending, (state) => {
        state.loading.stages = true;
        state.error.stages = null;
      })
      .addCase(fetchFunnelStages.fulfilled, (state, action: PayloadAction<FunnelStageInfo[]>) => {
        state.loading.stages = false;
        state.stages = action.payload;
        state.error.stages = null;
        state.lastFetch.stages = Date.now();
      })
      .addCase(fetchFunnelStages.rejected, (state, action) => {
        state.loading.stages = false;
        state.error.stages = action.payload as string;
      });

    // === FETCH FUNNEL STAGE INFO ===
    builder
      .addCase(fetchFunnelStageInfo.pending, (state) => {
        state.loading.stageInfo = true;
        state.error.stageInfo = null;
      })
      .addCase(fetchFunnelStageInfo.fulfilled, (state, action: PayloadAction<FunnelStageInfo | null>) => {
        state.loading.stageInfo = false;
        state.currentStageInfo = action.payload;
        state.error.stageInfo = null;
        state.lastFetch.stageInfo = Date.now();
      })
      .addCase(fetchFunnelStageInfo.rejected, (state, action) => {
        state.loading.stageInfo = false;
        state.error.stageInfo = action.payload as string;
      });

    // === FETCH FUNNEL STAGE VALUES ===
    builder
      .addCase(fetchFunnelStageValues.pending, (state) => {
        state.loading.stageValues = true;
        state.error.stageValues = null;
      })
      .addCase(fetchFunnelStageValues.fulfilled, (state, action: PayloadAction<FunnelStage[]>) => {
        state.loading.stageValues = false;
        state.stageValues = action.payload;
        state.error.stageValues = null;
        state.lastFetch.stageValues = Date.now();
      })
      .addCase(fetchFunnelStageValues.rejected, (state, action) => {
        state.loading.stageValues = false;
        state.error.stageValues = action.payload as string;
      });
  },
});

// === EXPORTS ===

export const { clearErrors, clearCurrentStageInfo, resetFunnelState } = funnelSlice.actions;
export default funnelSlice.reducer;

// === SELECTORS ===

export const selectFunnelStages = (state: { funnel: FunnelState }) => state.funnel.stages;
export const selectFunnelStageValues = (state: { funnel: FunnelState }) => state.funnel.stageValues;
export const selectCurrentStageInfo = (state: { funnel: FunnelState }) => state.funnel.currentStageInfo;

export const selectFunnelLoading = (state: { funnel: FunnelState }) => state.funnel.loading;
export const selectFunnelErrors = (state: { funnel: FunnelState }) => state.funnel.error;

export const selectFunnelStageByValue = (state: { funnel: FunnelState }, value: FunnelStage) =>
  state.funnel.stages.find(stage => stage.value === value);

export const selectIsFunnelDataLoaded = (state: { funnel: FunnelState }) =>
  state.funnel.stages.length > 0 && !state.funnel.loading.stages;

export const selectFunnelStageOrdered = (state: { funnel: FunnelState }) =>
  [...state.funnel.stages].sort((a, b) => a.order - b.order);
